import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Data endpoints
export const uploadFile = async (file, crop = null) => {
  const formData = new FormData();
  formData.append('file', file);
  
  let url = '/api/data/upload';
  if (crop) url += `?crop=${encodeURIComponent(crop)}`;

  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes timeout for large files
  });
  return response.data;
};

export const getUploadStatus = async (jobId) => {
  const response = await api.get(`/api/data/upload/status/${jobId}`);
  return response.data;
};

export const getAllTables = async (crop = null) => {
  let url = '/api/data/tables';
  if (crop) url += `?crop=${encodeURIComponent(crop)}`;
  const response = await api.get(url);
  return response.data;
};

export const getTableData = async (tableName, skip = 0, limit = 10, radFilter = null, nameSearch = null) => {
  let url = `/api/data/table/${tableName}?skip=${skip}&limit=${limit}`;
  if (radFilter) url += `&rad_filter=${radFilter}`;
  if (nameSearch) url += `&name_search=${encodeURIComponent(nameSearch)}`;
  const response = await api.get(url);
  return response.data;
};

export const getUploadedFiles = async () => {
  const response = await api.get('/api/data/files');
  return response.data;
};

export const deleteTable = async (tableName) => {
  const response = await api.delete(`/api/data/table/${tableName}`);
  return response.data;
};

export const addCultivarsToTable = async (tableName, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post(`/api/data/add-cultivars/${tableName}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Analytics endpoints
export const getTableStatistics = async (tableName, columnName) => {
  const response = await api.get(`/api/analytics/table/${tableName}/stats?column_name=${columnName}`);
  return response.data;
};

export const getTopValues = async (tableName, columnName, orderBy, n = 10) => {
  const response = await api.get(`/api/analytics/table/${tableName}/top?column_name=${columnName}&order_by=${orderBy}&n=${n}`);
  return response.data;
};

export const getDataSummary = async (crop = null) => {
  let url = '/api/analytics/data/summary';
  if (crop) url += `?crop=${encodeURIComponent(crop)}`;
  const response = await api.get(url);
  return response.data;
};

// Phenotype endpoints
export const getMasterTables = async (token, crop = null) => {
  let url = '/api/phenotype/master-tables';
  if (crop) url += `?crop=${encodeURIComponent(crop)}`;
  const response = await api.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const retrieveSubset = async (token, tableName, markerFile, cultivarFile) => {
  const formData = new FormData();
  formData.append('table_name', tableName);
  if (markerFile) {
    formData.append('marker_file', markerFile);
  }
  if (cultivarFile) {
    formData.append('cultivar_file', cultivarFile);
  }
  
  const response = await api.post('/api/phenotype/retrieve-subset', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

// ── SNP Chip Exploration endpoints ──

export const getSnpMarkers = async (tableName, search = '') => {
  let url = `/api/data/table/${tableName}/snp-markers`;
  if (search) url += `?search=${encodeURIComponent(search)}`;
  const response = await api.get(url);
  return response.data;
};

export const getSampleCultivars = async (tableName, search = '', radFilter = null) => {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (radFilter) params.append('rad_filter', radFilter);
  const qs = params.toString();
  const response = await api.get(`/api/data/table/${tableName}/sample-cultivars${qs ? '?' + qs : ''}`);
  return response.data;
};

export const getAvailableYears = async (tableName) => {
  const response = await api.get(`/api/data/table/${tableName}/years`);
  return response.data;
};

export const previewSubset = async (tableName, filters) => {
  const response = await api.post(`/api/data/table/${tableName}/preview-subset`, filters, {
    timeout: 120000,
  });
  return response.data;
};

export const downloadSubset = async (tableName, filters) => {
  const response = await api.post(`/api/data/table/${tableName}/download-subset`, filters, {
    responseType: 'blob',
    timeout: 1800000,  // 30 minutes for large full-dataset downloads
  });
  return response;
};

// ── Crop / Species Metadata endpoints ──

export const getCrops = async () => {
  const response = await api.get('/api/data/crops');
  return response.data;
};

export const assignCrop = async (tableName, crop, description = '') => {
  const response = await api.post('/api/data/crops/assign', {
    table_name: tableName, crop, description,
  });
  return response.data;
};

export const getTablesWithCrops = async () => {
  const response = await api.get('/api/data/tables-with-crops');
  return response.data;
};

export const getTablesByCrop = async (crop) => {
  const response = await api.get(`/api/data/tables-by-crop/${encodeURIComponent(crop)}`);
  return response.data;
};

export default api;
